import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Age Experience Edit Modal ---------------------------------------------
type AgeKey = 'infant' | 'toddler' | 'school';

const AGE_LABEL: Record<AgeKey, { title: string; detail: string; suffix: string }> = {
  infant: {
    title: 'Infants',
    detail: '(under 1 year old)',
    suffix: 'infants',
  },
  toddler: {
    title: 'Toddlers',
    detail: '(1-3 years old)',
    suffix: 'toddlers',
  },
  school: {
    title: 'School age',
    detail: '(4+ years old)',
    suffix: 'school age children',
  },
};

const formatExperienceLabel = (suffix: string, label: string): string => {
  if (label.indexOf('Not willing') > -1) {
    return `${label} ${suffix}`;
  }
  return label;
};

export const AgeExperienceEditModal = ({
  isOpen,
  onClose,
  ageExperiences,
  initialExperiences,
  onSave,
}: ModalBase & {
  ageExperiences: Array<{ id: number; label: string }>;
  initialExperiences?: {
    infant?: { id?: number; label?: string };
    toddler?: { id?: number; label?: string };
    school?: { id?: number; label?: string };
  };
  onSave: (experiences: {
    infant?: { id: number };
    toddler?: { id: number };
    school?: { id: number };
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [infantId, setInfantId] = useState<number | ''>(
    initialExperiences?.infant?.id ?? '',
  );
  const [toddlerId, setToddlerId] = useState<number | ''>(
    initialExperiences?.toddler?.id ?? '',
  );
  const [schoolId, setSchoolId] = useState<number | ''>(
    initialExperiences?.school?.id ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInfantId(initialExperiences?.infant?.id ?? '');
      setToddlerId(initialExperiences?.toddler?.id ?? '');
      setSchoolId(initialExperiences?.school?.id ?? '');
    }
  }, [isOpen, initialExperiences]);

  // Partial selections are allowed; the earlier all-or-nothing guard silently aborted when any experience id was unset or 0.
  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave({
        infant: infantId !== '' ? { id: Number(infantId) } : undefined,
        toddler: toddlerId !== '' ? { id: Number(toddlerId) } : undefined,
        school: schoolId !== '' ? { id: Number(schoolId) } : undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const renderGroup = (
    key: AgeKey,
    value: number | '',
    setter: (v: number | '') => void,
  ): JSX.Element => (
    <div className='mb-4'>
      <div className='font-semibold'>
        {AGE_LABEL[key].title} <span className='font-normal'>{AGE_LABEL[key].detail}</span>
      </div>
      <select
        id={`edit-sitter-exp-${key === 'school' ? 'school-age' : key}`}
        aria-label={`${AGE_LABEL[key].title} experience`}
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mt-1'
        value={value}
        onChange={(e): void => setter(e.target.value ? Number(e.target.value) : '')}
      >
        <option value=''>Select</option>
        {ageExperiences.map((exp) => (
          <option key={exp.id} value={exp.id}>
            {formatExperienceLabel(AGE_LABEL[key].suffix, exp.label)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editAgeExperience')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-age-experience'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <h4 className='mb-3'>
        What are the ages you are willing to babysit? How many years of experience do you have?
      </h4>
      {renderGroup('infant', infantId, setInfantId)}
      {renderGroup('toddler', toddlerId, setToddlerId)}
      {renderGroup('school', schoolId, setSchoolId)}
    </Modal>
  );
};
